/**
 * L'organisation du transport des matériaux de NOS carrières : combien de camions, quelles heures.
 *
 * *Demande de Chris, 09/09/2026 : « lorsqu'il y a un matériau du groupe, le conducteur va
 * dispatcher les pastilles de tonnage par jour en précisant si besoin les heures limites, il
 * choisira aussi les ressources camions — 8×4 ou 4×2 — et la ressource MOI chauffeur. À ce
 * moment-là tu auras toutes les informations pour positionner automatiquement et correctement les
 * pastilles de la ressource camion et chauffeur. […] Tu es capable d'optimiser et de positionner
 * le nombre de camions et de chauffeurs. »*
 *
 * ## Ce qui rend le calcul possible, et pourquoi il n'est pas nouveau
 *
 * Tout est déjà là : `voyagesNecessaires` compte les tours depuis le 04/09, `detailDuVoyage` dit
 * ce qu'un tour coûte, la charge utile vient du couple d'attelage paramétré le 01/09 et la
 * distance de la table du site. **Ce module n'invente aucune arithmétique** — il en tire une
 * conclusion de plus : à combien d'exemplaires il faut être pour que le travail d'un jour tienne
 * dans un jour.
 *
 * C'est la règle du calcul unique tenue un cran plus loin : le contrôle du transport et cette
 * organisation lisent le **même** tour. Deux calculs finiraient par annoncer deux durées pour la
 * même rotation, et personne ne saurait laquelle croire.
 *
 * ## Le périmètre est étroit, et c'est Chris qui l'a fermé
 *
 * *« Attention je parle uniquement quand on est sur du transport par nos soins, groupe coché et
 * matériaux de nos carrières. »* Trois conditions, toutes vérifiées avant de calculer quoi que ce
 * soit. Hors de là le module **se tait** : il rend un motif, jamais un nombre de camions par
 * défaut. Un transporteur affrété ne mobilise aucun de nos camions, et lui en compter serait
 * inventer une charge — c'est déjà la règle de `controlerLeTransport`.
 *
 * ## Ce que le module NE décide pas
 *
 * **L'heure limite ne resserre rien.** Une pastille peut demander « avant 17 h » ; pour en tirer
 * une contrainte il faudrait savoir à quelle heure la journée commence, et le produit ne porte
 * aucune heure de début — le planning vit en demi-journées. Inventer 7 h ou 8 h serait poser une
 * règle du métier que personne n'a donnée. L'heure limite est donc **recopiée** sur la rotation,
 * où le chauffeur la lira, et le calcul dit ce qu'il en fait : rien.
 *
 * **Le tonnage ne se déplace pas d'un jour à l'autre.** Le conducteur a dispatché ses pastilles ;
 * lisser 180 T sur deux jours pour économiser un camion reviendrait à défaire son geste. Chaque
 * jour est dimensionné pour lui-même.
 *
 * ## Les trajets du dépôt : UN ALLER PAR CAMION, chaque matin, et jamais de retour
 *
 * **État au 10/09/2026**, après deux corrections successives de Chris. La règle tient en deux
 * phrases :
 *
 *   - *« S'il y a deux camions alors il faut compter 1 aller Pouydesseaux vers la carrière par
 *     camion, donc 2 avant le 1er voyage du matin ; s'il y a trois camions alors 3 fois. »* Et,
 *     sur le camion resté à la carrière la veille : *« non, le camion repart toujours du
 *     dépôt. »* L'approche se multiplie donc par la flotte du jour, tous les jours.
 *   - **Le retour du soir ne se compte nulle part**, et c'est une simplification assumée :
 *     *« afin de simplifier les calculs on ne comptera pas le retour au dépôt le soir, mais
 *     comme tu comptes un aller-retour carrière cela compensera parfois. »* Le dernier tour de
 *     la journée paie un retour vers la carrière que le camion ne fait pas toujours ; il couvre
 *     approximativement le retour au dépôt qu'on n'a pas compté. Sur MAILLAS, approximativement
 *     veut dire 38,9 km comptés contre 47,9 km réels — la compensation est partielle, Chris l'a
 *     posée en le sachant.
 *
 * Sur MAILLAS : **4 allers** (3 camions le jour 2, 1 le jour 3), aucun retour, **29 h 15**.
 *
 * ### Ce que cette règle a remplacé, et pourquoi ne pas y revenir
 *
 * Première version du 09/09 : l'approche par camion et par jour, **plus** le retour pour chaque
 * camion dont la journée atteignait un seuil. Sur MAILLAS cela faisait 6 approches et 4 retours,
 * soit 352 km et **7 h de plus que le compte de Chris**, qui refait le calcul ainsi :
 *
 * > *« 14 × (38.9 × 2) = 1089.2 km – 1 retour 1er soir – 32.2 = 1057 km. Donc 2 aller
 * > pouydesseaux a carrieres 1 depart du matin = 64.40 + 1057 = 1121.4 km. 1121 km à 50 km/h =
 * > 1345 minutes + 14 tours × 10 minutes manip et 10 minutes chargement = 1625 minutes = 27 h. »*
 *
 * Cette arithmétique-là ne compte **qu'un seul** trajet dépôt → carrière pour tout l'ouvrage :
 * deux départs du matin moins le retour du premier soir. Le module l'a suivie à la lettre le
 * 09/09 au soir — et Chris l'a corrigée le lendemain, en précisant que ce « 2 » comptait des
 * **camions** et non des jours. L'ouvrage entier n'en portait plus qu'un ; il en porte
 * maintenant un par camion et par jour, ce qui est la lecture qui redonne son sens à sa phrase.
 *
 * **Ce qui ne revient pas, en revanche, c'est le retour du soir** — il reste imputé sur le
 * chantier où le camion enchaîne, sa règle du 09/09 : *« s'il travaille sur un autre chantier
 * en fin de journée le temps de retour sera imputé sur l'autre chantier »*. C'est lui, et non
 * l'approche, qui portait l'essentiel des 7 h refusées (4 retours à 47,9 km contre 2 approches
 * de trop à 32,2 km).
 *
 * **La capacité d'un camion, elle, ne bouge pas** : l'aller du matin ne se retient pas sur sa
 * journée, un camion tient toujours `floor(9 ÷ 1 h 53) = 4` chargements, et MAILLAS garde ses
 * **3 camions** au jour 2 — le nombre que Chris attendait en demandant *« pourquoi 3
 * camions ? »*. Le pourquoi de ce choix est développé dans `organiserLeTransport`.
 *
 * *Ce que ce module calcule, et il faut le savoir en le lisant :* la grandeur qui se compare au
 * chiffrage. Elle tient maintenant le kilométrage d'approche réel de la flotte — trois camions
 * font bien trois fois 32,2 km — mais toujours pas ses retours du soir.
 */
import { dec } from "./nombre.js";
import { detailDuVoyage, enTonnes, voyagesNecessaires } from "./controle-transport.js";
/** Le mode d'acheminement qui vaut « par nos soins ». Le même que partout ailleurs. */
const PAR_NOS_SOINS = "interne";
/**
 * Combien de TOURS COMPLETS un camion fait dans une journée.
 *
 * **Exportée pour que l'écran de paramétrage montre le même nombre.** Il affiche l'effet de la
 * journée du chauffeur en chargements — « 9 h, donc 4 chargements pour une carrière à 38,9 km » —,
 * et le refaire de son côté finirait par annoncer un nombre que le calcul ne pose pas. C'est la
 * même règle du calcul unique qui fait lire le même tour au contrôle et à cette organisation.
 *
 * **Un tour entamé à 16 h 30 ne rentre pas dans une journée qui finit à 17 h.** C'est la même
 * règle que `voyagesNecessaires`, prise par l'autre bout : un demi-voyage n'existe pas, ni pour
 * emporter les dernières tonnes, ni pour remplir la fin d'une journée.
 *
 * La journée est pleine et entière : l'aller du dépôt ne s'y réserve pas, bien qu'il soit compté
 * par camion depuis le 10/09/2026 — voir l'encadré de `organiserLeTransport`, c'est un choix
 * assumé et non un oubli. Et c'est celle du **chauffeur**, réglable au paramétrage transport —
 * 9 h, non les 8 h d'une équipe de chantier.
 *
 * Au moins un, toujours : une carrière plus loin qu'une journée de roulage donnerait zéro tour
 * par camion, donc une division par zéro et un nombre de camions infini. Ce cas se signale par
 * `journeePleine`, il ne fait pas tomber le calcul.
 */
export function toursParCamion(dureeDuTour, disponible) {
    const duree = dec(dureeDuTour);
    const journee = dec(disponible);
    if (duree.lessThanOrEqualTo(0) || journee.lessThanOrEqualTo(0))
        return 1;
    return Math.max(1, Number(journee.div(duree).floor().toFixed()));
}
/**
 * Combien de camions pour faire ces tours dans la journée.
 *
 * ## Pourquoi les TOURS et non les heures — question de Chris, 09/09/2026
 *
 * La première version divisait les heures : `ceil(20,78 ÷ 8) = 3`. Elle donne la bonne réponse
 * sur MAILLAS, et elle est fausse — elle autorise un camion à faire **une fraction de tour**.
 *
 * Le contre-exemple est une carrière à 70 km, soit un tour de 3 h : un camion y fait **2** tours
 * dans sa journée, jamais 2,67. Cinq tours à faire, c'est 15 h ; la division des heures répond
 * deux camions, qui ne peuvent en faire que quatre. Le cinquième tour n'était fait par personne,
 * et rien ne l'aurait dit — c'est exactement la journée de camion qui manque au planning que
 * l'arrondi des voyages existe pour empêcher.
 *
 * Sur MAILLAS les deux formules répondent 3 et 1 : un tour de 1 h 53 laisse 4 tours par camion,
 * et `ceil(11 ÷ 4) = 3`. C'est pourquoi aucun essai ne l'avait attrapée.
 */
function camionsNecessaires(voyages, tours) {
    if (voyages <= 0)
        return 0;
    return Math.max(1, Math.ceil(voyages / tours));
}
/**
 * Dimensionne le transport d'un ouvrage, jour par jour.
 *
 * `chargesUtiles` porte une entrée par ligne de camion choisie sur l'ouvrage. **Elles
 * s'additionnent** (règle du 27/08/2026 : un porteur et sa benne forment un attelage), et un
 * maillon **sans tonnage déclaré est ignoré** — un tracteur ne porte rien, c'est la benne qui
 * porte. Le calcul ne se ferme que si aucun maillon n'en déclare : voir `sommeDesCharges`.
 */
export function organiserLeTransport(entree) {
    const motifs = [];
    /*
      LE PÉRIMÈTRE D'ABORD, ET ENTIÈREMENT.
  
      Les trois conditions de Chris sont vérifiées avant tout calcul, et toutes les trois sont
      rendues : sur une ligne qui n'en remplit aucune, dire seulement la première obligerait à
      corriger trois fois pour découvrir trois refus successifs.
    */
    if (entree.modeLivraison !== PAR_NOS_SOINS)
        motifs.push("pas_par_nos_soins");
    if (!entree.duGroupe || !entree.carriereDesignee)
        motifs.push("pas_du_groupe");
    const { total: chargeUtile, sansCharge } = sommeDesCharges(entree.chargesUtiles);
    if (entree.chargesUtiles.length === 0)
        motifs.push("aucun_camion");
    else if (chargeUtile === null || dec(chargeUtile).lessThanOrEqualTo(0)) {
        motifs.push("charge_utile_inconnue");
    }
    if (entree.distanceKm === null)
        motifs.push("distance_inconnue");
    if (entree.pastilles.length === 0)
        motifs.push("aucune_pastille");
    const parVoyage = entree.distanceKm === null ? null : detailDuVoyage(entree.distanceKm, entree.reglages);
    const heuresChiffrees = dec(entree.heuresChiffrees).toFixed();
    const rien = {
        journees: [],
        chargeUtile,
        maillonsSansCharge: sansCharge,
        parVoyage,
        distanceKm: entree.distanceKm,
        distanceDepotCarriereKm: entree.distanceDepotCarriereKm,
        distanceChantierDepotKm: entree.distanceChantierDepotKm,
        heuresTotales: "0",
        heuresChiffrees,
        motifs,
    };
    if (motifs.length > 0 || chargeUtile === null || parVoyage === null)
        return rien;
    /*
      LE TRAJET DU DÉPÔT (09/09/2026, corrigé le soir même).
  
      Une distance inconnue vaut ZÉRO ici, et c'est le seul endroit du module où c'est vrai. Ailleurs
      un zéro se lirait « c'est à côté » et fausserait tout ; ici il se lit « ce trajet n'est pas
      compté », l'écran affiche la distance absente, et le dimensionnement des tours reste juste.
      Fermer le calcul entier pour une distance entre deux sites du groupe aurait rendu le bouton
      inutilisable sur les cinq sites dont la table n'est pas complète.
  
      Le retour chantier → dépôt ne se lit plus du tout : il est imputé ailleurs.
    */
    const approche = entree.distanceDepotCarriereKm === null || !(entree.reglages.vitesseKmh > 0)
        ? dec(0)
        : dec(entree.distanceDepotCarriereKm).div(entree.reglages.vitesseKmh);
    /*
      LA JOURNÉE D'UN CAMION RESTE PLEINE, alors même que son aller lui est de nouveau compté.
  
      C'est un choix, et il n'est pas tranché par le métier. L'aller du matin occupe bien 39
      minutes de la journée du chauffeur ; les lui retenir donnerait `9 − 0,644 = 8,356 h`, donc
      parfois un chargement de moins et un camion de plus.
  
      Deux raisons de ne pas le faire tant que Chris ne l'a pas demandé. **Sa correction du
      10/09 porte sur les kilomètres comptés, jamais sur le dimensionnement** — il a validé
      3 camions au jour 2 de MAILLAS et rien ne dit qu'il en veuille 4. Et cette retenue ne
      change *rien* sur MAILLAS (8,356 ÷ 1,8893 = 4,42, soit les mêmes 4 chargements) : elle ne
      déplacerait donc que des chantiers qu'on n'a pas sous les yeux, ce qui est la pire façon
      de changer un nombre de camions.
  
      Et c'est la journée du CHAUFFEUR, réglée au paramétrage : 9 h, pas les 8 h d'une équipe.
    */
    const tours = toursParCamion(parVoyage.heures, dec(entree.heuresJourneeChauffeur));
    const journees = [];
    let total = dec(0);
    /*
      Les jours se parcourent dans l'ORDRE, parce que le premier porte l'approche.
  
      Les pastilles arrivent dans l'ordre où le conducteur les a posées, qui n'est pas forcément
      celui du calendrier : trier est ce qui garantit que le trajet du dépôt tombe sur le jour où le
      camion quitte réellement Pouydesseaux, et non sur le premier de la liste.
    */
    const parJour = [...entree.pastilles].sort((a, b) => a.jourRelatif - b.jourRelatif);
    for (const pastille of parJour) {
        const tonnes = enTonnes(pastille.quantite, entree.unite);
        if (tonnes === null) {
            // Une seule pastille non convertible ferme l'ouvrage entier : poser des rotations pour
            // une partie du tonnage donnerait un planning qui a l'air complet et ne l'est pas.
            return { ...rien, motifs: ["unite_non_convertible"] };
        }
        const chargements = voyagesNecessaires(tonnes, chargeUtile);
        // `voyagesNecessaires` ne rend `null` que sur une charge nulle, déjà écartée plus haut.
        if (chargements === null || chargements === 0)
            continue;
        /*
          UN SEUL COMPTE DE TOURS, ENTIER — règle de Chris du 10/09/2026.
    
          Deux nombres cohabitaient : les tours *comptés* (10,5882) qui faisaient les heures, et les
          *chargements* au supérieur (11) qui dimensionnaient la flotte. *« Il faudrait s'arranger
          pour que les nombres de tours tombent juste, donc pas de décimal […] cela doit tomber juste
          par jour. »* L'arrondi au plus proche donne la même réponse aux deux, et il n'en reste
          qu'un.
    
          **Les heures suivent donc les tours entiers**, ce qui les fait bouger : sur MAILLAS, le
          jour 2 passe de 10,5882 à 11 tours, soit +0,78 h. C'est voulu — un camion ne fait pas
          0,5882 tour, et Chris a explicitement écarté la précision décimale au profit d'un compte
          qui tombe juste.
        */
        const heuresDesTours = dec(parVoyage.heures).times(chargements);
        const camions = camionsNecessaires(chargements, tours);
        /*
          CHAQUE CAMION PART DU DÉPÔT, CHAQUE MATIN — règle de Chris du 10/09/2026.
    
          *« S'il y a deux camions alors il faut compter 1 aller Pouydesseaux vers la carrière par
          camion, donc 2 avant le 1er voyage du matin ; s'il y a trois camions alors 3 fois. »* Puis,
          interrogé sur le camion resté à la carrière la veille : *« non, le camion repart toujours
          du dépôt. »*
    
          L'approche se multiplie donc par le nombre de camions du jour, et elle tombe sur tous les
          jours — non plus sur le seul premier. Sur MAILLAS : 3 allers le jour 2, 1 le jour 3.
        */
        const trajetDepot = approche.times(camions);
        /*
          LE REMPLISSAGE, camion par camion — question de Chris du 09/09/2026 au soir.
    
          Les premiers font le plein, le dernier prend le reste : la même forme que la répartition des
          heures du 04/09, et pour la même raison. Lisser ferait qu'aucun camion ne paraisse jamais
          plein, et c'est précisément ce que la moyenne de 6,88 h donnait à lire.
    
          Ce sont les chargements ENTIERS qui se répartissent, pas les tours comptés : la question est
          « qu'est-ce que ce camion fait de sa journée ? », et il fait des trajets entiers.
        */
        const chargementsParCamion = [];
        let aPlacer = chargements;
        for (let n = 0; n < camions; n++) {
            const siens = Math.min(tours, aPlacer);
            aPlacer -= siens;
            chargementsParCamion.push(siens);
        }
        /*
          LES JOURNÉES SE REMPLISSENT — demande de Chris, 09/09/2026, redite trois fois.
    
          Les `camions − 1` premiers font le plein et sont identiques : une pastille, l'effectif les
          multiplie. Le dernier prend **le reste des heures** — pas ses tours entiers —, ce qui garde
          le total égal à celui du tonnage : 10,5882 tours et non 11.
    
          Sur MAILLAS au jour 2 : deux camions à 4 chargements, soit 7 h 33 de roulage chacun, puis
          un camion à 4 h 53 de reste — et **chacun des trois** ajoute ses 0 h 39 de dépôt. Total
          21 h 56 : deux journées pleines et une courte, au lieu de trois demi-journées inexistantes.
    
          **CHAQUE pastille porte son trajet de dépôt depuis le 10/09/2026**, et non plus la seule
          dernière. Tous les camions partent de Pouydesseaux le matin : en charger un seul aurait
          affiché deux journées à 7 h 33 qui commencent en réalité 39 minutes plus tôt.
        */
        const pleins = camions - 1;
        const roulageDUnPlein = dec(parVoyage.heures).times(tours);
        const heuresDUnPlein = roulageDUnPlein.plus(approche);
        const resteDuDernier = heuresDesTours.minus(roulageDUnPlein.times(pleins)).plus(approche);
        const pastilles = [];
        if (pleins > 0) {
            pastilles.push({
                unites: pleins,
                heures: heuresDUnPlein.toFixed(),
                chargements: tours,
                porteLeTrajetDuDepot: approche.greaterThan(0),
            });
        }
        pastilles.push({
            unites: 1,
            heures: resteDuDernier.toFixed(),
            chargements: chargements - tours * pleins,
            porteLeTrajetDuDepot: approche.greaterThan(0),
        });
        const heuresTotales = heuresDesTours.plus(trajetDepot);
        journees.push({
            jourRelatif: pastille.jourRelatif,
            pastilleId: pastille.id,
            heureLimite: pastille.heureLimite,
            tonnes,
            chargements,
            heuresDesTours: heuresDesTours.toFixed(),
            trajetDepotHeures: trajetDepot.toFixed(),
            heuresTotales: heuresTotales.toFixed(),
            camions,
            toursParCamion: tours,
            chargementsParCamion,
            heuresReellesParCamion: chargementsParCamion.map((c) => dec(parVoyage.heures).times(c).toFixed()),
            pastilles,
            journeePleine: camions > 1,
        });
        total = total.plus(heuresTotales);
    }
    if (journees.length === 0)
        return { ...rien, motifs: ["aucune_pastille"] };
    return {
        journees,
        chargeUtile,
        maillonsSansCharge: sansCharge,
        parVoyage,
        distanceKm: entree.distanceKm,
        distanceDepotCarriereKm: entree.distanceDepotCarriereKm,
        distanceChantierDepotKm: entree.distanceChantierDepotKm,
        heuresTotales: total.toFixed(),
        heuresChiffrees,
        motifs: [],
    };
}
/**
 * La somme des charges utiles déclarées, et la liste de ceux qui n'en portent aucune.
 *
 * ## Un maillon sans tonnage ne ferme plus le calcul — correction du 09/09/2026
 *
 * *Retour de Chris sur l'affaire de CAMPET : « un des camions n'a pas de charge, c'est parce que
 * quand il y a tracteur il faut se baser sur les bennes, et pour les bennes tu as le paramétrage
 * transport. »*
 *
 * La migration 052 le disait déjà, du classeur de Chris : l'attelage `TRACTEUR 4X2 + BENNE ALU`
 * vaut 31 tonnes, et *« un tracteur seul ne porte rien »*. Le tonnage vit sur la ligne de la
 * benne ; celle du tracteur n'en a pas, et c'est une réponse et non une lacune. Fermer l'ouvrage
 * entier là-dessus rendait le bouton inutilisable sur tout attelage tracté.
 *
 * **On somme donc ce qui est déclaré, et on ignore le reste.** Le calcul ne se ferme que si
 * *aucun* maillon ne porte de tonnage : là, il n'y a vraiment rien à charger.
 *
 * ## Ce que ça coûte, et comment on le paie
 *
 * Un tonnage simplement OUBLIÉ au paramétrage — une benne dont personne n'a saisi la charge —
 * devient indistinguable d'un tracteur. Le calcul continuerait alors sur la seule charge du
 * porteur, annoncerait plus de camions qu'il n'en faut, et rien ne dirait pourquoi. C'est
 * exactement l'état faux silencieux que le `null` d'origine cherchait à empêcher.
 *
 * D'où les codes rendus : **l'écran nomme chaque maillon non compté**. La règle de Chris est
 * suivie, et un oubli reste visible à l'endroit où il fausse le résultat — au lieu d'être
 * arbitré par un code qui devinerait ce qu'est un tracteur.
 */
function sommeDesCharges(charges) {
    const sansCharge = charges.filter((m) => m.tonnage === null).map((m) => m.code);
    const declarees = charges.filter((m) => m.tonnage !== null);
    if (declarees.length === 0)
        return { total: null, sansCharge };
    let somme = dec(0);
    for (const maillon of declarees)
        somme = somme.plus(maillon.tonnage ?? "0");
    return { total: somme.toFixed(), sansCharge };
}
//# sourceMappingURL=transport-materiaux.js.map